import React, { useState } from 'react';
import { startCase, kebabCase } from 'lodash';
import moment from 'moment';
import classnames from 'classnames';

import {
  DailySummaryDto,
  SubstanceUsageType,
  TriggerDTO,
} from '@peartherapeutics/hylia-api-client';
import Dot from './Dot';
import Bup from './Bup';
import { HorizontalScrollbar } from '../../Widgets/CustomScrollbars';

import { legends } from '../../../utils';

import styles from './styles.module.scss';

interface ICravingsProps {
  showBupUseLabels: boolean;
  dailySummaries: DailySummaryDto[];
  onHoverCraving: (triggers: TriggerDTO[]) => void;
  onLeaveCraving: () => void;
  timezone: string;
}

const CravingChart = (props: ICravingsProps) => {
  const [hoverIndex, setHoverIndex] = useState<number>(-1);

  const onHoverCraving = (triggers: TriggerDTO[], index: number) => () => {
    props.onHoverCraving(triggers);
    setHoverIndex(index);
  };

  const onLeaveCraving = () => {
    props.onLeaveCraving();
    setHoverIndex(-1);
  };

  const { dailySummaries, showBupUseLabels } = props;

  const header = (
    <div>
      <div className={styles.title}>
        <div aria-hidden>Substance Use & Cravings</div>
        <div className={styles['title-hidden']}>
          Substance use and cravings chart
        </div>
      </div>
      <ul className={styles['top-legend-wrapper']} aria-hidden>
        {legends.map((item) => (
          <li className={styles[kebabCase(item)]} key={item}>
            {startCase(item)}
          </li>
        ))}
      </ul>
    </div>
  );

  const chart = (
    <div className={styles['craving-section']} data-testid="craving-section">
      <div className={styles['craving-yaxis-label']}>range 0 to 10</div>
      <ul className={styles['y-axis']} aria-hidden>
        <li>10</li>
        <li>5</li>
        <li>0</li>
      </ul>

      <HorizontalScrollbar
        isShown={dailySummaries.length > 14}
        style={{ width: 448, height: 241, maxHeight: 241 }}
      >
        <div className={styles.chart} data-testid="substance-use-chart">
          {/* ==== FOR EACH DAILY SUMMARY, RENDER A <BAR /> COMPONENT ==== */}
          {dailySummaries.map((summary: DailySummaryDto, index: number) => {
            const date = moment(summary.date).tz(props.timezone);

            let columnLabel = '';
            switch (summary.medicationUse) {
              case SubstanceUsageType.NoUse:
                columnLabel = 'Substance not used';
                break;

              case SubstanceUsageType.Used:
                columnLabel = 'Substance used';
                break;

              default:
                columnLabel = 'No data reported';
            }

            return (
              <div className={styles.block} key={`bar-${summary.date}`}>
                {/* ==== THESE ARE FOR ACCESSIBILITY FOR SCREEN READERS ==== */}
                <div
                  aria-hidden
                  className={classnames(
                    index % 3 !== 1 ? styles['text-hidden'] : '',
                    styles['axis-legend'],
                  )}
                >
                  {date.format('MM/DD')}
                </div>
                <div className={classnames(styles['axis-legend-hidden'])}>
                  {`${date.format('MMMM')}, ${date.format('Do')}`}
                </div>
                <div className={styles['column-legend']}>{columnLabel}</div>

                {/* ==== BAR ==== */}
                <div
                  className={classnames(
                    styles['craving-column'],
                    styles[summary.substanceUse],
                    {
                      [styles.hover]: hoverIndex === index,
                    },
                  )}
                >
                  <Dot
                    key={`craving-dot-${date}`}
                    intensity={summary.averageCravingIntensity}
                    onMouseEnter={onHoverCraving(summary.triggers, index)}
                    onMouseLeave={onLeaveCraving}
                  />

                  {showBupUseLabels && (
                    <Bup key={`bup-dot-${date}`} summary={summary} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </HorizontalScrollbar>
    </div>
  );

  const bupLabels = (
    <div
      className={
        showBupUseLabels
          ? styles['bottom-legend-wrapper']
          : classnames(styles['bottom-legend-wrapper'], styles.disabled)
      }
    >
      <div>
        <span className={styles['bup-nonuse']} />
        <span aria-hidden="true">Buprenorphine Non-Use</span>
      </div>

      <div>
        <span className={styles['bup-used']} />
        <span aria-hidden="true">Buprenorphine Use</span>
      </div>
    </div>
  );

  return (
    <div className={styles.wrapper} data-testid="craving-chart">
      <div className={styles.section}>
        {header}
        {chart}
        {bupLabels}
      </div>
    </div>
  );
};

export default CravingChart;
